// eslint-disable-next-line simple-import-sort/imports
import { format, isAfter } from 'date-fns';
import { ChangeEvent } from 'react';

import { trimClassname } from '@/utils/styleHelper';
import {
    markFieldFocus,
    trackSelection,
    uniqueIdTransfer,
    useFieldValidation,
} from '@/apiCalls/uniqueIDTransferHelper';
import { EEventType } from '../eventType';
import { FieldLabel } from '../FieldLabel';

export const testId = 'DateInput';
export interface IDateInput {
    title: string;
    placeholder: string;
    onChange: (value: Date) => void;
    showErrors: boolean;
    id?: string;
    disabled?: boolean;
    value?: Date;
    minDate?: Date;
    maxDate?: Date;
    dateFormat?: string;
    tooltipContent?: string;
    /** Field wrappers are required by default; optional fields opt out. */
    required?: boolean;
}

export function DateInput({
    title,
    placeholder,
    id,
    value,
    showErrors,
    disabled = false,
    onChange,
    minDate,
    maxDate,
    dateFormat = 'yyyy-MM-dd',
    tooltipContent,
    required = true,
}: IDateInput): JSX.Element {
    useFieldValidation(id, showErrors);

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        let date = new Date(value);

        // A native date input reports an empty value until all three parts are
        // filled, so every keystroke on the way to a complete date produced an
        // Invalid Date here. That was passed straight on, stored as the literal
        // string "Invalid Date", and read back as unparseable - so the field
        // greyed out and lost what had been typed. Partial input is now left
        // alone until it becomes a date.
        if (isNaN(date.getTime())) return;

        if (maxDate && date > maxDate) {
            date = maxDate;
        }

        onChange(date);

        uniqueIdTransfer({
            eventType: EEventType.INPUT,
            applicationData: { id: id },
        });
        trackSelection(id);
    };

    function getDisplayValue(): string {
        if (!value) return '';
        // An INVALID Date is still an object, so `!value` does not catch it and
        // date-fns' format() throws RangeError: Invalid time value. A throw here happens
        // during render, so it does not fail politely — it takes down the whole React
        // tree and the applicant sees a blank widget. Reproduced by DisplayScreen's
        // TEST_DRIVE_LANDING_PAGE case, which mounts a DateInput with no usable value.
        //
        // Empty string matches formatDatetoString, which already guards the same way:
        // an unparseable date renders as nothing rather than as an error.
        // Coerced rather than assumed: the prop is typed as a Date but callers pass the
        // raw stored value, which can be a string — `value.getTime is not a function`.
        const asDate = value instanceof Date ? value : new Date(value);
        if (isNaN(asDate.getTime())) return '';
        if (maxDate && isAfter(value, maxDate))
            return format(maxDate, dateFormat);
        return format(value, dateFormat);
    }

    return (
        <div data-testid={testId} className='py-2 mx-auto w-full max-w-md'>
            <FieldLabel
                title={title}
                tooltipContent={tooltipContent}
                required={required}
            />
            <input
                className={trimClassname(
                    `appearance-none border rounded-[10px] w-full p-2 text-textPrimary leading-tight h-11 inputButtonFocus placeholderStyle ${
                        getDisplayValue() === '' ? 'emptyDateTime' : ''
                    } ${showErrors ? errorClass : ''} ${
                        disabled
                            ? 'hover:none cursor-disabled'
                            : 'hover:shadow '
                    }`
                )}
                id={id}
                type='date'
                data-testid='inputField'
                placeholder={placeholder}
                value={getDisplayValue()}
                onChange={handleChange}
                onFocus={(): void => markFieldFocus(id)}
                disabled={disabled}
                min={minDate ? format(minDate, dateFormat) : undefined}
                max={maxDate ? format(maxDate, dateFormat) : undefined}
                pattern={dateFormat}
            />
        </div>
    );
}
export const errorClass = 'error';
